import { Card, CardContent } from '@/components/ui/card';
import { CampaignCreationCardHeader } from '@/components/glitch-ui/campaigncreationcardheader';
import { BusinessCustomerItem } from '@/components/glitch-ui/business-customer-item';
import { useQuery } from '@tanstack/react-query';
import { businessIdealCustomersQueryOptions } from '@/lib/query-options';
import { BusinessGenerateLoading } from '../new/business-generate-loading';
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
interface CampaignBusinessCustomerProps {
  businessUrl: string;
  onNext: () => void;
  setBusinessCustomer: (customer: string) => void;
  setStepValid: (valid: boolean) => void;
}

export const CampaignBusinessCustomer = ({
  businessUrl,
  onNext: _onNext,
  setBusinessCustomer,
  setStepValid,
}: CampaignBusinessCustomerProps) => {
  const { isLoading, data: idealCustomers } = useQuery(businessIdealCustomersQueryOptions(businessUrl));
  const [selectedCustomer, setSelectedCustomer] = useState<string | null>(null);

  const handleCustomerSelect = (customer: string) => {
    setSelectedCustomer(customer);
    setBusinessCustomer(customer);
  };

  // Enable continue button once loaded — selection is optional
  // If no customer is selected, default to all customers joined
  useEffect(() => {
    if (!isLoading && idealCustomers && !selectedCustomer) {
      setBusinessCustomer(idealCustomers.join(', '));
    }
    setStepValid(!isLoading);
  }, [isLoading, selectedCustomer, idealCustomers]);

  return (
    <motion.div 
      className='campaign-goal-select flex-row space-y-6 !mt-0'
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeInOut" }}
    >
      {isLoading ? (
        <BusinessGenerateLoading 
          showBackButton={true}
          onBackClick={() => {
            // For queries, we can't cancel but we can provide feedback to parent component
            // This would typically allow the user to go back to previous step
            setStepValid(false);
          }}
          backButtonText="Cancel"
        />
      ) : (
        <Card className='border-none shadow-none'>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <CampaignCreationCardHeader
              title=''
              subTitle="Choose the customer profile that best matches your target audience. This helps us create more effective ad copy and targeting strategies."
            />
          </motion.div>
          <CardContent className='m-6 p-0 space-y-6'>
            <motion.div 
              className='bg-slate-100 rounded-lg p-1 space-y-1 max-h-[40vh] overflow-y-auto'
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              {idealCustomers &&
                idealCustomers.map((customer, index) => (
                  <motion.div
                    key={`business-ideal-customer-${index}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.3 + index * 0.1 }}
                  >
                    <BusinessCustomerItem
                      customer={customer}
                      selected={selectedCustomer === customer}
                      onSelect={handleCustomerSelect}
                    />
                  </motion.div>
                ))}
            </motion.div>
          </CardContent>
        </Card>
      )}
    </motion.div>
  );
};
